import { RTEInitValue, extractTextWithNewLinesFromHtml } from '@talentmesh/rte';
import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { ApplicantInformation } from '../../Models/ApplicantInformation';
import { DefaultJobAd, JobAd } from '../../Models/JobAd';
import { JobDetails } from '../../Models/JobDetails';
import { DemoQuestions, mapDemoQuestionsDto2Model } from '../../Models/Questions';
import { mapTestInfoDto2Model } from '../../Models/TestInfo';
import JobAdMapper from '../../Steps/JobAdStep/Builder/JobAdMapper';
import { jobDetailsDeepEqual } from '../../Steps/JobDetailsStep/JobDetailsStepUtils';
import { OneClickRecruitmentPrefillResponse } from '../api/useOneClickCreateRecruitment';
import { mapOneClickPrefillToState } from '../OneClickPrefillMapper';
import {
    CreateRecruitmentContextState,
    DefaultCreateRecruitmentContextState,
    mapCreateRecruitmentContextState2Dto,
} from './CreateRecruitmentContextState';
import { useJobFunctions } from '@/api/useJobFunctions';
import { useCompanyContext } from '@/Context/CompanyContext';
import { ClientResponse, DefaultClientResponse } from '@/DTO/Clients/ClientResponse';
import { UpdateClientRequest } from '@/DTO/Clients/UpdateClientRequest';
import GenerateExperienceCriteriaDTO, {
    createGenerateExperienceCriteriaDTO,
} from '@/DTO/ExperienceCriteria/GenerateExperienceCriteriaDTO';
import AIJobAdDTO from '@/DTO/JobAds/AIJobAdDTO';
import GenerateJobAdDTO from '@/DTO/JobAds/GenerateJobAdDTO';
import {
    useAssessmentClient,
    useAutocompleteOptionsClient,
    useClientsClient,
    useCriteriaClient,
    useJobAdClient,
    useJobTestInfoClient,
    useQuestionsConfigurationClient,
    useSettingsClient,
} from '@/Hooks/ClientHooks';
import useClientIdPathParam from '@/Hooks/UseClientIdPathParam';
import { TestTypes } from '@/Models/Configuration';
import { Currency } from '@/Models/Currency';
import { mapGeneratedStringToExperienceCriteria } from '@/Models/ExperienceCriteria';
import ProcessingState from '@/Models/ProcessingState';
import HttpErrorTypes from '@/Utils/HTTP/Errors/HttpErrorType';
import TooManyRequestsError from '@/Utils/HTTP/Errors/TooManyRequestsError';
import { isDefaultLanguage } from '@/Utils/JobAdLanguage/JobAdLanguages';
import { getJobFunctionName } from '@/Utils/JobFunctionSelectionUtils';

export interface ICreateRecruitmentContext extends CreateRecruitmentContextState {
    client: ClientResponse;
    currencies: Currency[];
    locales: string[];
    gettingDataProcessingState: ProcessingState;

    setContextState: (value: CreateRecruitmentContextState) => void;
    setIncludedTests: (value: TestTypes[]) => void;
    setApplicantInformation: (value: ApplicantInformation[]) => void;
    setProcessingState: (value: ProcessingState) => void;
    setJobAd: (newJobAd: JobAd) => void;
    setTestLanguage: (value: string) => void;
    setExperienceCriteria: (valueSet: {
        experienceCriteria: CreateRecruitmentContextState['experienceCriteria'];
    }) => void;
    resetExperienceCriteriaProcessingState: () => void;
    generateExperienceCriteria: (newJobAd: JobAd) => Promise<void>;

    updateJobDetails: (newJobDetails: JobDetails) => Promise<void>;
    createRecruitmentAsync: (
        clientId: string,
        newExperienceCriteria: CreateRecruitmentContextState['experienceCriteria'],
    ) => Promise<void>;
    generateJobAdAsync: () => Promise<void>;
    getDemoQuestions: (testLang: string) => Promise<DemoQuestions>;

    resolvedCompanyDescription: string;
    shouldGenerateJobAdCompanyDescription: () => boolean;
    closeDialog: () => void;
    setShouldRegenExperienceCriteria: (value: CreateRecruitmentContextState['regenerateExperienceCriteria']) => void;

    // One-click fallback: pre-fill the manual wizard from an extracted-but-invalid job ad.
    applyOneClickPrefill: (prefill: OneClickRecruitmentPrefillResponse) => void;
}

export const CreateRecruitmentContext = createContext<ICreateRecruitmentContext | undefined>(undefined);

const CreateRecruitmentContextProvider = CreateRecruitmentContext.Provider;

interface CreateRecruitmentContextProviderProps {
    children: ReactNode;
}

export const CreateRecruitmentProvider = ({ children }: CreateRecruitmentContextProviderProps) => {
    const { updateCompanyProfile, currentCompanyProfile } = useCompanyContext();
    const { companyName, companyDescription, companyUrl } = currentCompanyProfile;

    const clientIdFromPath = useClientIdPathParam();

    const [state, setState] = useState<CreateRecruitmentContextState>({
        ...DefaultCreateRecruitmentContextState,
    });

    const {
        recruitmentId,
        clientId,
        jobDetails,
        jobAd,
        includedTests,
        testInfos,
        applicantInformation,
        processingState,
        regenerateAtStep2,
        regenerateExperienceCriteria,
        showErrorDialog,
        testLanguage,
        experienceCriteria,
        experienceCriteriaProcessingState,
        isJobAdPrefilled,
        isExperienceCriteriaPrefilled,
    } = state;

    const assessmentClient = useAssessmentClient();
    const jobTestInfoClient = useJobTestInfoClient();
    const autocompleteOptionsClient = useAutocompleteOptionsClient();
    const settingsClient = useSettingsClient();
    const jobAdClient = useJobAdClient();
    const { data: jobFunctionsMap } = useJobFunctions();
    const questionsConfigurationClient = useQuestionsConfigurationClient();
    const criteriaClient = useCriteriaClient();
    const clientsClient = useClientsClient();

    const [client, setClient] = useState<ClientResponse>(DefaultClientResponse);
    const [currencies, setCurrencies] = useState<Currency[]>([]);
    const [locales, setLocales] = useState<string[]>([]);

    // Job ad company description is taken either from:
    // - tenant settings (company description) if client is default (My company)
    // - client description if client is not default
    // We use this state to store resolved company description in the job ad.
    const [resolvedCompanyDescription, setResolvedCompanyDescription] = useState<string>('');
    const [gettingDataProcessingState, setGettingDataProcessingState] = useState(ProcessingState.Processing);

    const getDemoQuestions = useCallback(
        async (testLang: string) => {
            const demoQuestionsDto = await jobTestInfoClient.getDemoQuestionsAsync(
                jobDetails.jobFunctionId,
                testLang === 'applicant' ? 'en' : testLang,
            );
            const demoQuestions = mapDemoQuestionsDto2Model(demoQuestionsDto);
            return demoQuestions;
        },
        [jobDetails.jobFunctionId],
    );

    const setProcessingState = (value: ProcessingState): void => {
        setState((prevState) => {
            return {
                ...prevState,
                processingState: value,
            };
        });
    };

    /*
     * Function to resolve company description in the job ad
     * - If client is default (My company) then company description is taken from the tenant settings
     * - If client is not default, then company description is taken from client description
     * @returns resolved company description which is used for job ad company description
     * */
    const resolveJobAdCompanyDescription = (clientResponse: ClientResponse): string => {
        const jobAdInitialDescription = clientResponse.isDefault
            ? companyDescription
            : (clientResponse.description ?? RTEInitValue);
        setResolvedCompanyDescription(jobAdInitialDescription);
        return jobAdInitialDescription;
    };

    const shouldGenerateJobAdCompanyDescription = (): boolean => {
        // Company description is generated in the following cases:
        // 1. Resolved company description is empty in the settings
        // 2. Resolved company description is not empty and job ad language is not default
        // 3. If job posting type is on behalf but on no named basis then company description should be generated
        const isResolvedCompanyDescriptionEmpty = extractTextWithNewLinesFromHtml(resolvedCompanyDescription) === '';
        const shouldGenerateCompanyDescription =
            isResolvedCompanyDescriptionEmpty ||
            !isDefaultLanguage(jobDetails.jobDescriptionLanguage) ||
            jobDetails.jobPostingType === 'OnBehalfNoNamedBasis';

        return shouldGenerateCompanyDescription;
    };

    useEffect(() => {
        // loads all required data on create recruitment page initialization
        const loadInfoAsync = async () => {
            try {
                const currenciesList = await settingsClient.getCurrenciesAsync();
                setCurrencies(currenciesList);

                const localesList = await questionsConfigurationClient.getTestLocalesAsync();
                setLocales(localesList);

                const retrievedClient = await clientsClient.getClientByIdAsync(clientIdFromPath);
                setClient(retrievedClient);

                // set job posting type depending on client type
                setState((prevState) => {
                    return {
                        ...prevState,
                        jobDetails: {
                            ...prevState.jobDetails,
                            jobPostingType: retrievedClient.isDefault ? 'Direct' : 'OnBehalf',
                        },
                    };
                });

                // once client is loaded we can resolve company description which should be used for job ad
                resolveJobAdCompanyDescription(retrievedClient);

                setGettingDataProcessingState(ProcessingState.Success);
            } catch {
                setGettingDataProcessingState(ProcessingState.Error);
            }
        };
        loadInfoAsync();
    }, []);

    const setContextState = (value: CreateRecruitmentContextState): void => {
        setState(value);
    };

    const setJobAd = (value: JobAd): void => {
        setState((prevState) => {
            return {
                ...prevState,
                jobAd: { ...value },
                regenerateAtStep2: false,
            };
        });
    };

    const setIncludedTests = (value: TestTypes[]): void => {
        setState((prevState) => {
            return {
                ...prevState,
                includedTests: value,
            };
        });
    };

    const setApplicantInformation = (value: ApplicantInformation[]): void => {
        setState((prevState) => {
            return {
                ...prevState,
                applicantInformation: value,
            };
        });
    };

    const setTestLanguage = (value: string): void => {
        setState((prevState) => {
            return {
                ...prevState,
                testLanguage: value,
            };
        });
    };

    const includeTest = (contextState: CreateRecruitmentContextState, testType: TestTypes): boolean => {
        if (contextState.testInfos.findIndex((x) => x.testType === testType) >= 0) {
            contextState.includedTests.push(testType);
            return true;
        }

        return false;
    };

    const updateJobDetails = async (newJobDetails: JobDetails): Promise<void> => {
        setProcessingState(ProcessingState.Processing);
        try {
            const { jobFunctionId } = newJobDetails;
            const testInfosDto = await jobTestInfoClient.getTestInfosAsync(jobFunctionId);
            const newTestInfos = testInfosDto.map((dto) => mapTestInfoDto2Model(dto));
            const equal = jobDetailsDeepEqual(jobDetails, newJobDetails);
            let newJobAd = equal || isJobAdPrefilled ? jobAd : DefaultJobAd;

            if (!equal && !isJobAdPrefilled) {
                const didJobFunctionChange = jobDetails.jobFunctionId !== newJobDetails.jobFunctionId;
                const { hardSkills, softSkills } = didJobFunctionChange
                    ? await autocompleteOptionsClient.getDefaultSkillsOptions(jobFunctionId)
                    : { hardSkills: jobAd.hardSkills, softSkills: jobAd.softSkills };

                newJobAd = {
                    ...newJobAd,
                    companyDescription: isDefaultLanguage(newJobDetails.jobDescriptionLanguage)
                        ? resolvedCompanyDescription
                        : jobAd.companyDescription,
                    hardSkills,
                    hardSkillsSwitch: didJobFunctionChange ? hardSkills.length > 0 : jobAd.hardSkillsSwitch,
                    softSkills,
                    softSkillsSwitch: didJobFunctionChange ? softSkills.length > 0 : jobAd.softSkillsSwitch,
                };
            }

            const newContextState: CreateRecruitmentContextState = {
                ...state,
                recruitmentId: '',
                jobDetails: newJobDetails,
                jobAd: newJobAd,
                includedTests: [],
                testInfos: newTestInfos,
                applicantInformation: [],
                processingState: ProcessingState.Success,
                regenerateAtStep2: !equal && !isJobAdPrefilled,
            };

            includeTest(newContextState, TestTypes.Personality);
            if (!includeTest(newContextState, TestTypes.Skills)) {
                includeTest(newContextState, TestTypes.Aptitude);
            }
            if (newContextState.jobDetails.directReports !== 'None') {
                includeTest(newContextState, TestTypes.EmotionalIntelligence);
            }

            setContextState(newContextState);
        } catch (e) {
            setProcessingState(ProcessingState.Error);
        }
    };

    const createRecruitmentAsync = async (
        clientIdToCreateFor: string,
        newExperienceCriteria: CreateRecruitmentContextState['experienceCriteria'],
    ): Promise<void> => {
        setProcessingState(ProcessingState.Processing);
        try {
            const dto = mapCreateRecruitmentContextState2Dto({
                ...state,
                experienceCriteria: newExperienceCriteria,
            });
            await assessmentClient.createAssessmentAsync(clientIdToCreateFor, dto);

            // Only update company description under the settings if:
            // - company description is empty AND
            // - job ad language is default (English)
            if (resolvedCompanyDescription === RTEInitValue && isDefaultLanguage(jobDetails.jobDescriptionLanguage)) {
                // if client is default, the tenant settings are updated
                // otherwise, the client is updated
                if (client.isDefault) {
                    await updateCompanyProfile({
                        ...currentCompanyProfile,
                        companyDescription: jobAd.companyDescription,
                    });
                } else if (jobDetails.jobPostingType !== 'OnBehalfNoNamedBasis') {
                    // in no named basis case, client description is generated every time and should not be saved under client description
                    // preserve everything except company description
                    const updateClientRequest: UpdateClientRequest = {
                        name: client.name,
                        websiteURL: client.websiteURL,
                        linkedInURL: client.linkedInURL,
                        description: jobAd.companyDescription,
                    };
                    await clientsClient.updateClientAsync(client.id, updateClientRequest);
                }
            }
            setProcessingState(ProcessingState.Success);
        } catch (e) {
            setProcessingState(ProcessingState.Error);
            throw e;
        }
    };

    const mapAndSetJobAd = (value: AIJobAdDTO, skipCompanyDescription = false): void => {
        const ja = new JobAdMapper(value, jobAd.companyDescription).map();
        setState((prev: CreateRecruitmentContextState) => ({
            ...prev,
            jobAd: {
                ...prev.jobAd,
                title: ja.title,
                description: ja.description,
                descriptionLen: ja.descriptionLen,
                companyDescription: skipCompanyDescription ? prev.jobAd.companyDescription : ja.companyDescription,
            },
            processingState: ProcessingState.Success,
        }));
    };

    const generateJobAdAsync = async (): Promise<void> => {
        const shouldGenerateCompanyDescription = shouldGenerateJobAdCompanyDescription();
        try {
            setProcessingState(ProcessingState.Processing);

            const dto: GenerateJobAdDTO = {
                jobPostingType: jobDetails.jobPostingType,
                jobTitle: jobDetails.name,
                companyName,
                companyUrl: client.isDefault ? companyUrl : client.websiteURL || '',
                clientName: client.isDefault ? undefined : client.name,
                workArrangement: jobDetails.workArrangement,
                jobLocation: jobDetails.locationDetails.description,
                jobFunction: getJobFunctionName(jobFunctionsMap.jobCategories, jobDetails.jobFunctionId),
                workExperience: jobDetails.workExperience,
                employmentType: jobDetails.employmentType,
                includeCompanyDescription: shouldGenerateCompanyDescription,
                jobAdLanguage: jobDetails.jobDescriptionLanguage.label,
            };

            const response = await jobAdClient.generateJobAdAsync(dto);
            mapAndSetJobAd(response, !shouldGenerateCompanyDescription && !!response.companyDescription);
        } catch (error) {
            if (error instanceof TooManyRequestsError) {
                setState((prev) => ({
                    ...prev,
                    showErrorDialog: 'TooManyRequests',
                    processingState: ProcessingState.Idle,
                }));
            } else {
                setState((prev) => ({
                    ...prev,
                    showErrorDialog: 'JobAdGenerationError',
                    processingState: ProcessingState.Idle,
                }));
            }
        }
    };

    const closeDialog = () => {
        setState((prev) => {
            return {
                ...prev,
                showErrorDialog: undefined,
            };
        });
    };

    const generateExperienceCriteria = async (updatedJobAd: JobAd) => {
        setState((p) => ({
            ...p,
            experienceCriteriaProcessingState: ProcessingState.Processing,
            regenerateExperienceCriteria: false,
        }));

        try {
            const dto: GenerateExperienceCriteriaDTO = createGenerateExperienceCriteriaDTO(updatedJobAd, jobDetails);
            const response = await criteriaClient.generateCriteriaAsync(dto);
            setState((prev: CreateRecruitmentContextState) => ({
                ...prev,
                experienceCriteria: response.criteria.map(mapGeneratedStringToExperienceCriteria),
                experienceCriteriaProcessingState: ProcessingState.Success,
                includesExperienceCriteria: true,
            }));
        } catch (error) {
            if (error instanceof TooManyRequestsError) {
                setState((prev) => ({
                    ...prev,
                    experienceCriteriaProcessingState: ProcessingState.Error,
                    experienceCriteriaError: HttpErrorTypes.ResourceNotFound,
                }));
            } else {
                setState((prev) => ({ ...prev, experienceCriteriaProcessingState: ProcessingState.Error }));
            }
        }
    };

    const resetExperienceCriteriaProcessingState = () => {
        setState((prev) => ({ ...prev, experienceCriteriaProcessingState: ProcessingState.Idle }));
    };

    const setExperienceCriteria: ICreateRecruitmentContext['setExperienceCriteria'] = (newValue) => {
        setState((p) => ({
            ...p,
            experienceCriteria: newValue.experienceCriteria,
        }));
    };

    const setShouldRegenExperienceCriteria: ICreateRecruitmentContext['setShouldRegenExperienceCriteria'] = (value) => {
        setState((p) => ({
            ...p,
            regenerateExperienceCriteria: p.regenerateExperienceCriteria || value,
        }));
    };

    // Pre-fill the manual wizard from a one-click fallback: map the assembled DTO payload onto context state.
    const applyOneClickPrefill = (prefill: OneClickRecruitmentPrefillResponse): void => {
        const { jobDetails, jobAd, experienceCriteria } = mapOneClickPrefillToState(client, prefill, currencies);
        const shouldRegenerateJobAd = !jobAd.title || !jobAd.description || !jobAd.companyDescription;
        const shouldRegenerateExperienceCriteria = experienceCriteria.length === 0;

        setState((prev) => ({
            ...prev,
            jobDetails,
            jobAd,
            experienceCriteria,
            isJobAdPrefilled: !shouldRegenerateJobAd,
            regenerateExperienceCriteria: shouldRegenerateExperienceCriteria,
            isExperienceCriteriaPrefilled: !shouldRegenerateExperienceCriteria,
        }));
    };

    return (
        <CreateRecruitmentContextProvider
            value={{
                recruitmentId,
                clientId,
                jobDetails,
                jobAd,
                includedTests,
                testInfos,
                applicantInformation,
                processingState,
                regenerateAtStep2,
                regenerateExperienceCriteria,
                showErrorDialog,
                testLanguage,
                experienceCriteria,
                experienceCriteriaProcessingState,
                isJobAdPrefilled,
                isExperienceCriteriaPrefilled,

                generateExperienceCriteria,

                client,
                currencies,
                locales,
                gettingDataProcessingState,

                setContextState,
                setIncludedTests,
                setApplicantInformation,
                setProcessingState,
                setJobAd,
                setTestLanguage,
                updateJobDetails,
                createRecruitmentAsync,
                generateJobAdAsync,
                closeDialog,
                getDemoQuestions,
                setExperienceCriteria,
                resetExperienceCriteriaProcessingState,

                resolvedCompanyDescription,
                shouldGenerateJobAdCompanyDescription,
                setShouldRegenExperienceCriteria,

                applyOneClickPrefill,
            }}
        >
            {children}
        </CreateRecruitmentContextProvider>
    );
};

export function useCreateRecruitmentContext() {
    const context = useContext(CreateRecruitmentContext);
    if (!context) {
        throw new Error('useCreateRecruitmentContext must be used within the CreateRecruitmentContext.Provider');
    }
    return context;
}
